// ✅ src/pages/Sales/PartPaymentCustomerReport.jsx
// Reads selected id from URL (?id=xxx) -> calls GETBYID API -> fills header + payment history -> auto print
//
// Backend mount:
//   app.use("/api/partListing", partPaymentListing);
//
// Assumed API (adjust if your route name differs):
//   GET /api/partListing/getById?id=123
//
// Expected response shapes supported:
// A) { success:true, customer:{...}, payments:[...] }
// B) { data:{ customer:{...}, payments:[...] } }
// C) { customer:{...}, paymentHistory:[...] }
// D) { data:[...] } (then we try to infer)

import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";

const BASE_URL = import.meta?.env?.VITE_BASE_URL || "http://nissi-india.com";

const PartPaymentCustomerReport = () => {
  const location = useLocation();

  const [customerData, setCustomerData] = useState({});
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const qs = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const selectedId = qs.get("id"); // ✅ /Sales/PartPaymentReport_Print?id=123

  const formatNumber = (n) =>
    new Intl.NumberFormat("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(
      Number(n || 0)
    );

  const formatDate = (d) => {
    if (!d) return "";
    const dt = new Date(d);
    if (!isNaN(dt.getTime())) {
      const dd = String(dt.getDate()).padStart(2, "0");
      const mm = String(dt.getMonth() + 1).padStart(2, "0");
      const yy = dt.getFullYear();
      return `${dd}/${mm}/${yy}`;
    }
    // if backend already gives "25-03-2021" or "26/03/2021"
    return String(d);
  };

  // ✅ Total paid amount
  const totalPaidAmount = useMemo(() => {
    return paymentHistory.reduce((sum, item) => sum + Number(item.paidAmount || 0), 0);
  }, [paymentHistory]);

  // ---------------------------
  // ✅ Fetch by selected id
  // ---------------------------
  useEffect(() => {
    const fetchById = async () => {
      if (!selectedId) {
        setError("Missing id in URL. Open like: /Sales/PartPaymentReport_Print?id=123");
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // 🔧 Adjust if your backend uses different param name (custid/paymentid/etc.)
        const res = await axios.get(`${BASE_URL}/api/partListing/getById?id=${encodeURIComponent(selectedId)}`);

        const payload = res?.data;

        // ---- try multiple possible shapes ----
        const customer =
          payload?.customer ??
          payload?.data?.customer ??
          payload?.data?.header ??
          payload?.header ??
          payload?.data?.cust ??
          payload?.cust ??
          payload?.data ??
          payload ??
          {};

        const paymentsRaw =
          payload?.payments ??
          payload?.data?.payments ??
          payload?.paymentHistory ??
          payload?.data?.paymentHistory ??
          payload?.history ??
          payload?.data?.history ??
          payload?.details ??
          payload?.data?.details ??
          [];

        const paymentsArr = Array.isArray(paymentsRaw)
          ? paymentsRaw
          : Array.isArray(payload?.data) // sometimes backend returns array of rows
          ? payload.data
          : [];

        // ✅ map customer fields (adjust keys to match your backend)
        const mappedCustomer = {
          customerId: customer?.customerId ?? customer?.fld_customerid ?? customer?.fld_customerId ?? "",
          customerName: customer?.customerName ?? customer?.fld_customername ?? "",
          productName: customer?.productName ?? customer?.fld_productname ?? customer?.assetName ?? "",
          salesPerson: customer?.salesPerson ?? customer?.fld_salesperson ?? customer?.salesman ?? "",
          totalSqFeet: customer?.totalSqFeet ?? customer?.fld_totalsqfeet ?? customer?.area ?? "",
          plotNoAssetCode:
            customer?.plotNoAssetCode ??
            customer?.fld_plotno ??
            customer?.plotNumber ??
            customer?.fld_assetcode ??
            customer?.assetCode ??
            "",
          location: customer?.location ?? customer?.fld_location ?? "",
          dateOfBooking: formatDate(customer?.dateOfBooking ?? customer?.fld_dateofbooking ?? customer?.bookingDate),
          assetValue: formatNumber(customer?.assetValue ?? customer?.fld_assetvalue ?? customer?.purchaseValue ?? 0),
        };

        // ✅ map payment history fields (adjust keys)
        const mappedPayments = paymentsArr.map((p, idx) => ({
          sNo: idx + 1,
          date: formatDate(p?.date ?? p?.fld_date ?? p?.paymentDate),
          billNo: p?.billNo ?? p?.fld_billno ?? p?.billno ?? "",
          paidAmount: Number(p?.paidAmount ?? p?.fld_payingamount ?? p?.amount ?? 0),
          balanceAmount: Number(p?.balanceAmount ?? p?.fld_balanceamount ?? p?.balAmount ?? 0),
          nextPaymentDate: formatDate(p?.nextPaymentDate ?? p?.fld_nextpaymentdate ?? ""),
          paymentMode: p?.paymentMode ?? p?.fld_paymode ?? "",
          status: p?.status ?? p?.paymentStatus ?? p?.fld_paymentstatus ?? "",
        }));

        setCustomerData(mappedCustomer);
        setPaymentHistory(mappedPayments);

        // ✅ auto print
        setTimeout(() => window.print(), 400);
      } catch (e) {
        console.error(e);
        setError("Failed to load customer report by id");
        setCustomerData({});
        setPaymentHistory([]);
      } finally {
        setLoading(false);
      }
    };

    fetchById();
  }, [selectedId]);

  const printedDate = useMemo(() => {
    const dt = new Date();
    const dd = String(dt.getDate()).padStart(2, "0");
    const mm = String(dt.getMonth() + 1).padStart(2, "0");
    const yy = dt.getFullYear();
    return `${dd}/${mm}/${yy}`;
  }, []);

  return (
    <div className="max-w-4xl mx-auto p-4 bg-white font-sans text-sm print:p-0 print:max-w-none">
      {/* Header Section */}
      <div className="border-2 border-black mb-0">
        <div className="border-b border-black flex">
          {/* Logo Section */}
          <div className="w-32 border-r border-black p-2 flex items-center justify-center">
            <div className="bg-black text-white px-2 py-1 text-xs font-bold text-center">
              <div>NISSI</div>
              <div>INFOTECH PVT LTD</div>
            </div>
          </div>

          {/* Company Info Section */}
          <div className="flex-1 p-3">
            <div className="text-center">
              <h1 className="text-lg font-bold mb-1">Nissi Infotech</h1>
              <div className="text-xs">
                <div>Nissi</div>
                <div>3/42-12, Nagappa colony, kavundampalayam,641030, Tamil Nadu, India</div>
                <div>Phone No:9003744644 Email: infraconsoft-implementation@nissiinfotech.com</div>
              </div>
            </div>
          </div>
        </div>

        {/* Report Title */}
        <div className="text-center py-2 border-b border-black font-bold">Part Payment Customer</div>
      </div>

      {/* Loading/Error */}
      {loading && (
        <div className="text-center py-4">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-black"></div>
          <div className="mt-2">Loading customer data...</div>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded my-4">
          <div className="font-bold">Error:</div>
          <div>{error}</div>
        </div>
      )}

      {/* Customer Details Section */}
      <div className="border-l-2 border-r-2 border-black">
        <div className="grid grid-cols-2 text-sm">
          {/* Left Column */}
          <div className="border-r border-black">
            <div className="flex border-b border-gray-300 py-1">
              <div className="w-32 px-2 font-medium">Customer ID</div>
              <div className="px-2">: {customerData.customerId || ""}</div>
            </div>
            <div className="flex border-b border-gray-300 py-1">
              <div className="w-32 px-2 font-medium">Customer Name</div>
              <div className="px-2">: {customerData.customerName || ""}</div>
            </div>
            <div className="flex border-b border-gray-300 py-1">
              <div className="w-32 px-2 font-medium">Product Name</div>
              <div className="px-2">: {customerData.productName || ""}</div>
            </div>
            <div className="flex border-b border-gray-300 py-1">
              <div className="w-32 px-2 font-medium">Sales Person</div>
              <div className="px-2">: {customerData.salesPerson || ""}</div>
            </div>
            <div className="flex py-1">
              <div className="w-32 px-2 font-medium">Total Sq.Feet</div>
              <div className="px-2">: {customerData.totalSqFeet || ""}</div>
            </div>
          </div>

          {/* Right Column */}
          <div>
            <div className="flex border-b border-gray-300 py-1">
              <div className="w-32 px-2 font-medium">Plot No / Asset Code:</div>
              <div className="px-2">{customerData.plotNoAssetCode || ""}</div>
            </div>
            <div className="flex border-b border-gray-300 py-1">
              <div className="w-32 px-2 font-medium">Location</div>
              <div className="px-2">: {customerData.location || ""}</div>
            </div>
            <div className="flex border-b border-gray-300 py-1">
              <div className="w-32 px-2 font-medium">Date of Booking</div>
              <div className="px-2">: {customerData.dateOfBooking || ""}</div>
            </div>
            <div className="flex py-1">
              <div className="w-32 px-2 font-medium">Asset Value</div>
              <div className="px-2">: {customerData.assetValue || ""}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Payment History Table */}
      <div className="border-l-2 border-r-2 border-b-2 border-black">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-black bg-gray-100">
              <th className="border-r border-black px-2 py-2 w-12 text-center">S.No</th>
              <th className="border-r border-black px-2 py-2 w-20 text-center">Date</th>
              <th className="border-r border-black px-2 py-2 w-16 text-center">Bill No</th>
              <th className="border-r border-black px-2 py-2 w-24 text-center">Paid Amount</th>
              <th className="border-r border-black px-2 py-2 w-24 text-center">Balance Amount</th>
              <th className="border-r border-black px-2 py-2 w-24 text-center">Next Payment Date</th>
              <th className="border-r border-black px-2 py-2 w-20 text-center">Payment Mode</th>
              <th className="px-2 py-2 w-16 text-center">Status</th>
            </tr>
          </thead>

          <tbody>
            {paymentHistory.map((item, index) => (
              <tr key={index} className="border-b border-gray-300">
                <td className="border-r border-black px-2 py-2 text-center">{item.sNo}</td>
                <td className="border-r border-black px-2 py-2 text-center">{item.date}</td>
                <td className="border-r border-black px-2 py-2 text-center">{item.billNo}</td>
                <td className="border-r border-black px-2 py-2 text-right">{formatNumber(item.paidAmount)}</td>
                <td className="border-r border-black px-2 py-2 text-right">{formatNumber(item.balanceAmount)}</td>
                <td className="border-r border-black px-2 py-2 text-center">{item.nextPaymentDate}</td>
                <td className="border-r border-black px-2 py-2 text-center">{item.paymentMode}</td>
                <td className="px-2 py-2 text-center">{item.status}</td>
              </tr>
            ))}

            {/* Total Row */}
            <tr className="border-t-2 border-black">
              <td colSpan="3" className="border-r border-black px-2 py-2"></td>
              <td className="border-r border-black px-2 py-2 text-right font-bold border-2 border-black">
                {formatNumber(totalPaidAmount)}
              </td>
              <td colSpan="4" className="px-2 py-2"></td>
            </tr>

            {/* Empty rows */}
            {Array.from({ length: Math.max(0, 8 - paymentHistory.length) }).map((_, index) => (
              <tr key={`empty-${index}`} className="border-b border-gray-300 h-8">
                <td className="border-r border-black px-2 py-2"></td>
                <td className="border-r border-black px-2 py-2"></td>
                <td className="border-r border-black px-2 py-2"></td>
                <td className="border-r border-black px-2 py-2"></td>
                <td className="border-r border-black px-2 py-2"></td>
                <td className="border-r border-black px-2 py-2"></td>
                <td className="border-r border-black px-2 py-2"></td>
                <td className="px-2 py-2"></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Footer */}
      <div className="flex justify-end mt-8 text-xs">Printed Date : {printedDate}</div>

      {/* Print Styles */}
      <style jsx>{`
        @media print {
          body {
            margin: 0;
            -webkit-print-color-adjust: exact;
            color-adjust: exact;
          }
          .print\\:hidden {
            display: none !important;
          }
          .print\\:p-0 {
            padding: 0 !important;
          }
          .print\\:max-w-none {
            max-width: none !important;
          }
          table {
            border-collapse: collapse !important;
          }
          th,
          td {
            border: 1px solid black !important;
          }
          .bg-gray-100 {
            background-color: #f3f4f6 !important;
          }
          .bg-black {
            background-color: black !important;
            color: white !important;
          }
          @page {
            margin: 0.5in;
            size: A4 portrait;
          }
        }
      `}</style>
    </div>
  );
};

export default PartPaymentCustomerReport;
